import React from "react";
import { Search, ChevronDown, ChevronUp } from "lucide-react";
import { ConversationItem } from "./ConversationItem";
import type { Conversation } from "../../../types/index";

interface SidebarProps {
  isOpen: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  conversations: Conversation[];
  onConversationSelect: (id: number) => void;
  isMobile: boolean;
}

export function ChatSidebar({
  isOpen,
  searchQuery,
  onSearchChange,
  conversations,
  onConversationSelect,
  isMobile,
}: SidebarProps) {
  const [isPinnedExpanded, setIsPinnedExpanded] = React.useState(true);

  const pinnedConversations = conversations.filter((conv) => conv.isPinned);
  const regularConversations = conversations.filter((conv) => !conv.isPinned);

  const sidebarClasses = `
    ${isOpen ? "translate-x-0 bg-[#F6F8F9]" : "-translate-x-full bg-[#F6F8F9]"}
    transform transition-transform duration-300 ease-in-out
    ${
      isMobile
        ? "fixed inset-0 w-full bg-[#F6F8F9]"
        : "md:translate-x-0 md:static w-80 bg-[#F6F8F9]"
    }
    z-30 bg-white border-r border-gray-200 flex flex-col h-[calc(100vh-64px)]
  `;

  return (
    <div className={sidebarClasses}>
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center">
          <h2 className="text-lg font-medium text-[#546881]">
            Conversation en cours
          </h2>
        </div>
        <div className="mt-2 relative">
          <input
            type="text"
            placeholder="Rechercher une conversation..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {pinnedConversations.length > 0 && (
          <div>
            <button
              onClick={() => setIsPinnedExpanded(!isPinnedExpanded)}
              className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              Conversations épinglées ({pinnedConversations.length})
              {isPinnedExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
            {isPinnedExpanded && (
              <div className="border-b border-gray-200">
                {pinnedConversations.map((conversation) => (
                  <ConversationItem
                    key={conversation.id}
                    {...conversation}
                    onClick={() => onConversationSelect(conversation.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div>
          {regularConversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              {...conversation}
              onClick={() => onConversationSelect(conversation.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
